// ui/src/components/evaluation/RedFlagCard.tsx
import { useState } from 'react';
import { clsx } from 'clsx';
import type { NormalizedRedFlag } from '../../types';

interface RedFlagCardProps {
  flag: NormalizedRedFlag;
}

export function RedFlagCard({ flag }: RedFlagCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border border-red-600/50 bg-red-900/20">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 text-left"
      >
        <div className="flex items-center gap-2">
          <svg
            className="w-5 h-5 text-red-400 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="text-red-300 font-medium">{flag.label}</span>
        </div>
        <svg
          className={clsx(
            'w-4 h-4 text-red-400 transition-transform flex-shrink-0',
            isExpanded && 'rotate-180'
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-red-600/30 pt-3">
          {/* AI Rationale - if available */}
          {flag.rationale && (
            <div>
              <p className="text-wolf-500 text-xs uppercase tracking-wide mb-1">
                Why this was flagged
              </p>
              <p className="text-wolf-300 text-sm">{flag.rationale}</p>
            </div>
          )}

          {/* Description */}
          {flag.description && (
            <div>
              <p className="text-wolf-500 text-xs uppercase tracking-wide mb-1">
                What this means
              </p>
              <p className="text-wolf-400 text-sm">{flag.description}</p>
            </div>
          )}

          {/* Guidance */}
          {flag.guidance && (
            <div className="flex gap-2 bg-amber-500/10 border border-amber-500/30 p-2">
              <svg
                className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
              <div>
                <p className="text-amber-400 text-xs font-medium mb-0.5">Suggested Action</p>
                <p className="text-wolf-300 text-sm">{flag.guidance}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
